import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const PrivacyPage: React.FC = () => {
  const { t } = useLanguage();
  
  return (
    <div className="pt-24 pb-16">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
          
          <div className="prose prose-lg prose-invert">
            <p className="text-gray-300 mb-6">Last Updated: October 15, 2025</p>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">1. Introduction</h2>
              <p className="text-gray-300 mb-4">
                At TattooNailGenius, we value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service.
              </p>
              <p className="text-gray-300">
                Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the service.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">2. Information We Collect</h2>
              
              <h3 className="text-lg font-semibold mb-2">Personal Data</h3>
              <p className="text-gray-300 mb-4">
                While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you, including:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-300">
                <li>Email address</li>
                <li>Username</li>
                <li>Usage data</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-2">Usage Data</h3>
              <p className="text-gray-300 mb-4">
                We may also collect information about how the Service is accessed and used ("Usage Data"). This may include information such as:
              </p>
              <ul className="list-disc pl-6 text-gray-300">
                <li>Your computer's Internet Protocol address (e.g., IP address)</li>
                <li>Browser type and version</li>
                <li>The pages of our Service that you visit</li>
                <li>The time and date of your visit</li>
                <li>Design prompts and preferences</li>
              </ul>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">3. Use of Data</h2>
              <p className="text-gray-300 mb-4">
                TattooNailGenius uses the collected data for various purposes:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-300">
                <li>To provide and maintain our Service</li>
                <li>To notify you about changes to our Service</li>
                <li>To allow you to participate in interactive features of our Service</li>
                <li>To provide customer support</li>
                <li>To gather analysis or valuable information so that we can improve our Service</li>
                <li>To monitor the usage of our Service</li>
                <li>To detect, prevent and address technical issues</li>
              </ul>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">4. Data Security</h2>
              <p className="text-gray-300 mb-4">
                The security of your data is important to us, but remember that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal data, we cannot guarantee its absolute security.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">5. Data Retention</h2>
              <p className="text-gray-300 mb-4">
                We will retain your personal data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your personal data to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our legal agreements and policies.
              </p>
              <p className="text-gray-300">
                We will also retain Usage Data for internal analysis purposes. Usage Data is generally retained for a shorter period of time, except when this data is used to strengthen the security or to improve the functionality of our Service, or we are legally obligated to retain this data for longer periods.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">6. Disclosure of Data</h2>
              <h3 className="text-lg font-semibold mb-2">Legal Requirements</h3>
              <p className="text-gray-300 mb-4">
                TattooNailGenius may disclose your personal data in the good faith belief that such action is necessary to:
              </p>
              <ul className="list-disc pl-6 text-gray-300">
                <li>To comply with a legal obligation</li>
                <li>To protect and defend the rights or property of TattooNailGenius</li>
                <li>To prevent or investigate possible wrongdoing in connection with the Service</li>
                <li>To protect the personal safety of users of the Service or the public</li>
                <li>To protect against legal liability</li>
              </ul>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">7. Service Providers</h2>
              <p className="text-gray-300 mb-4">
                We may employ third-party companies and individuals to facilitate our Service ("Service Providers"), to provide the Service on our behalf, to perform Service-related services, or to assist us in analyzing how our Service is used.
              </p>
              <p className="text-gray-300">
                These third parties have access to your personal data only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">8. Your Data Protection Rights</h2>
              <p className="text-gray-300 mb-4">
                Depending on your location, you may have certain rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-300">
                <li>The right to access, update, or delete the information we have on you</li>
                <li>The right of rectification - the right to have your information corrected</li>
                <li>The right to object to our processing of your personal data</li>
                <li>The right of restriction - the right to request that we restrict the processing of your data</li>
                <li>The right to data portability</li>
                <li>The right to withdraw consent</li>
              </ul>
              <p className="text-gray-300">
                To exercise any of these rights, please contact us at info@mramoreno.tech.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">9. Changes to This Privacy Policy</h2>
              <p className="text-gray-300 mb-4">
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top.
              </p>
              <p className="text-gray-300">
                You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-bold mb-4">10. Contact Us</h2>
              <p className="text-gray-300">
                If you have any questions about this Privacy Policy, please contact us at info@mramoreno.tech.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;